import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  LayoutDashboard, Users, Briefcase, MessageSquare, Settings, LogOut,
  Calendar, Wallet, Bell, ChevronRight, Menu, X, BarChart3, Activity
} from 'lucide-react';

// ─────────────────────────────────────────────────
// NAV ITEMS
// ─────────────────────────────────────────────────
const mainNav = [
  { name: 'Dashboard', path: '/app/dashboard', icon: LayoutDashboard },
  { name: 'Campaigns', path: '/app/campaigns', icon: Briefcase },
  { name: 'Candidates', path: '/app/candidates', icon: Users },
  { name: 'Interviews', path: '/app/interviews', icon: Calendar },
  { name: 'Messages', path: '/app/messages', icon: MessageSquare },
];

const secondaryNav = [
  { name: 'Wallet', path: '/app/wallet', icon: Wallet },
  { name: 'Reports', path: '/app/reports', icon: BarChart3 },
  { name: 'Notifications', path: '/app/notifications', icon: Bell },
];

const masterNav = [
  { name: 'Master Panel', path: '/app/master-panel', icon: Settings },
  { name: 'All Applicants', path: '/app/master-applicants', icon: Users },
];

// ─────────────────────────────────────────────────
// BREADCRUMB HELPER
// ─────────────────────────────────────────────────
function useBreadcrumb() {
  const location = useLocation();
  const segments = location.pathname.replace('/app/', '').split('/').filter(Boolean);
  if (segments.length === 0) return 'Dashboard';
  return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')).join(' / ');
}

// ─────────────────────────────────────────────────
// MAIN LAYOUT
// ─────────────────────────────────────────────────
export default function DashboardLayout() {
  const { signOut, user, activeUserId, impersonatedId, impersonatedName, impersonatedPhone, setImpersonation } = useAuth();
  const navigate = useNavigate();
  const breadcrumb = useBreadcrumb();
  const [profile, setProfile] = useState<any>(null);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (activeUserId) {
      // Fetch recruiter profile + token balance + category
      supabase.from('recruiters').select('name, company_name, avatar_url, tokens_balance, category')
        .eq('id', activeUserId).single()
        .then(({ data }) => {
          if (data) {
            setProfile(data);
            setTokenBalance(data.tokens_balance || 0);
          }
        });

      // Fetch unread notification count
      supabase.from('notifications').select('id', { count: 'exact', head: true })
        .eq('user_id', activeUserId).eq('read', false)
        .then(({ count }) => setUnreadCount(count || 0));
    }
  }, [activeUserId]);

  const handleLogout = async () => {
    if (!window.confirm('Log out of jobisa?')) return;
    await signOut();
    navigate('/login');
  };

  const NavItem = ({ item, onClick }: { item: typeof mainNav[0]; onClick?: () => void }) => {
    const Icon = item.icon;
    return (
      <NavLink
        to={item.path}
        onClick={onClick}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-all duration-200 ${isActive
            ? 'bg-white/[0.08] text-white'
            : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
            <span>{item.name}</span>
            {item.name === 'Notifications' && unreadCount > 0 && (
              <span className="ml-auto w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ── MOBILE OVERLAY ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen w-[240px] bg-slate-950 text-white flex flex-col z-50
        transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center px-5 shrink-0">
          <NavLink to="/app/dashboard" className="flex items-center select-none group">
            <img 
              src="/wordmark.png" 
              alt="jobisa" 
              className="h-10 w-auto transition-all duration-300"
            />
          </NavLink>
        </div>

        {/* Main Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Main</p>
          {mainNav.map(item => <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />)}

          {profile?.category === 'master_dummy' && (
            <>
              <div className="h-px bg-white/[0.06] my-4" />
              <p className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-yellow-500 mb-3">Admin</p>
              {masterNav.map(item => <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />)}
            </>
          )}

          <div className="h-px bg-white/[0.06] my-4" />

          <p className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Tools</p>
          {secondaryNav.map(item => <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />)}
        </nav>

        {/* Token Balance Widget */}
        <div className="px-3 pb-3">
          <NavLink
            to="/app/wallet"
            className="block bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] rounded-xl p-3.5 transition-colors group"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Tokens</span>
              <Wallet size={14} className="text-slate-500 group-hover:text-blue-400 transition-colors" />
            </div>
            <p className="text-2xl font-black text-white leading-none tracking-tight">
              {tokenBalance.toLocaleString()}
            </p>
            <p className="text-[11px] text-slate-500 mt-1">Available balance</p>
          </NavLink>
        </div>

        {/* User + Settings + Logout */}
        <div className="border-t border-white/[0.06] p-3 space-y-1">
          <NavLink
            to="/app/settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-all ${isActive ? 'bg-white/[0.08] text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'
              }`
            }
          >
            <Settings size={18} strokeWidth={1.8} />
            Settings
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium text-slate-500 hover:text-red-400 hover:bg-red-500/[0.08] transition-all w-full text-left"
          >
            <LogOut size={18} strokeWidth={1.8} />
            Log Out
          </button>

          {/* User pill */}
          <div className="flex items-center gap-3 px-3 py-2 mt-1">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {profile?.name ? profile.name.charAt(0).toUpperCase() : 'R'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-white truncate">{profile?.name || 'Recruiter'}</p>
              <p className="text-[10px] text-slate-500 truncate">{profile?.company_name || 'Company'}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        
        {/* Impersonation Banner */}
        {impersonatedId && (
          <div className="bg-yellow-500 text-black px-4 py-2 flex items-center justify-between shadow-md z-40">
            <div className="flex items-center gap-2 font-bold text-sm">
              <Activity size={16} />
              <span>MANAGING RECRUITER: <span className="underline">{impersonatedName}</span> <span className="ml-2 bg-black/10 px-2 py-0.5 rounded text-[11px]">{impersonatedPhone}</span></span>
            </div>
            <button 
              onClick={() => setImpersonation(null, null, null)}
              className="bg-black text-white px-3 py-1 rounded-lg text-xs font-black hover:bg-slate-800 transition-all"
            >
              EXIT MANAGEMENT
            </button>
          </div>
        )}

        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-slate-200/60 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 mr-3"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[13px]">
            <span className="text-slate-400 font-medium">Portal</span>
            <ChevronRight size={12} className="text-slate-300" />
            <span className="text-slate-800 font-semibold">{breadcrumb}</span>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Notification bell */}
            <NavLink
              to="/app/notifications"
              className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </NavLink>

            {/* User avatar */}
            <NavLink to="/app/settings" className="flex items-center gap-2.5 hover:bg-slate-50 rounded-lg px-2 py-1.5 transition-colors">
              <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">
                {profile?.name ? profile.name.charAt(0).toUpperCase() : 'R'}
              </div>
              <div className="hidden md:block">
                <p className="text-[12px] font-semibold text-slate-800 leading-tight">{profile?.name || 'Recruiter'}</p>
                <p className="text-[10px] text-slate-400">{impersonatedPhone || profile?.company_name || ''}</p>
              </div>
            </NavLink>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
